import { z } from "zod";
import {
  ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH,
  ADS_CAMPAIGN_PLATFORM_HEADLINE_MAX_LENGTH,
  ADS_CAMPAIGN_LINKEDIN_CAROUSEL_CARD_HEADLINE_MAX_LENGTH,
  ADS_CAMPAIGN_LINKEDIN_CAROUSEL_CARD_HEADLINE_LEADS_MAX_LENGTH,
  ADS_CAMPAIGN_LINKEDIN_CAROUSEL_MIN_CARDS,
  ADS_CAMPAIGN_LINKEDIN_CAROUSEL_MAX_CARDS,
  ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION,
  LINKEDIN,
} from "../../../../../utils/constants";
import i18next from "i18next";
import {
  FIELD_MAX_LENGTH,
  FIELD_REQUIRED,
  FIELD_URL_VALID_WITH_PROTOCOL,
  FIELD_MIN_CARDS_AD_CAROUSEL,
  FIELD_MAX_IMAGES_VALID,
} from "../../../../../i18n/keysTranslations";
import { REGEX_URL_WITH_PROTOCOL_REQUIRED } from "../../../../../utils/regex";

// Ad format constants
const AD_FORMAT_IMAGE = "STANDARD_UPDATE";
const AD_FORMAT_VIDEO = "SINGLE_VIDEO";
const AD_FORMAT_CAROUSEL = "CAROUSEL";

export const assetsGroupLinkedinBaseSchema = z.object({
  platform: z.literal(LINKEDIN),
  id: z.string(),
  destinationUrls: z.any().optional(),
  callToAction: z.any().optional(),
  titles: z.any().optional(),
  headlines: z.any().optional(),
  descriptions: z.any().optional(),
  mediaUrls: z.any().optional(),
  leadForm: z.any().optional(),
});

export const validateAssetsGroupLinkedin = ({
  campaign,
  assetsGroupID,
  assetsGroup,
  ctx,
}) => {
  const platformData = campaign.platformsData?.[LINKEDIN];
  const adFormat = platformData?.adFormat;
  const objective = platformData?.objective;
  const basicPaths = ["assetsGroups", assetsGroupID];

  // If no adFormat selected, skip validation (the form will show a message)
  if (!adFormat) {
    return;
  }

  // Common validations for all formats

  // Primary text (titles[0]) - required for all formats
  const primaryText = assetsGroup.titles?.[0]?.value;
  if (!primaryText || primaryText.trim() === "") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "titles", 0, "value"],
      message: i18next.t(FIELD_REQUIRED),
    });
  } else if (
    primaryText.length > ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[LINKEDIN]
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "titles", 0, "value"],
      message: i18next.t(FIELD_MAX_LENGTH, {
        value: ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[LINKEDIN],
      }),
    });
  }

  // Lead form validation - required if objective is leads
  if (objective === ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION) {
    const leadFormId = assetsGroup.leadForm?.id;
    if (!leadFormId || leadFormId.trim() === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "leadForm"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }
  }

  // Format-specific validations
  if (adFormat === AD_FORMAT_IMAGE) {
    validateImageFormat({ assetsGroup, basicPaths, ctx });
  } else if (adFormat === AD_FORMAT_VIDEO) {
    validateVideoFormat({ assetsGroup, basicPaths, ctx });
  } else if (adFormat === AD_FORMAT_CAROUSEL) {
    validateCarouselFormat({ assetsGroup, basicPaths, ctx, objective });
  }
};

// Image format validation (STANDARD_UPDATE)
function validateImageFormat({ assetsGroup, basicPaths, ctx }) {
  const destinationUrls = assetsGroup.destinationUrls || [];

  // callToAction - required for all formats
  if (!assetsGroup.callToAction) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "callToAction"],
      message: i18next.t(FIELD_REQUIRED),
    });
  }

  // destinationUrl - required
  const destinationUrl = destinationUrls[0]?.value;
  if (!destinationUrl || destinationUrl.trim() === "") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "destinationUrls", 0, "value"],
      message: i18next.t(FIELD_REQUIRED),
    });
  } else if (
    !REGEX_URL_WITH_PROTOCOL_REQUIRED.test(encodeURI(destinationUrl))
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "destinationUrls", 0, "value"],
      message: i18next.t(FIELD_URL_VALID_WITH_PROTOCOL),
    });
  }

  // Headline (headlines[0]) - required
  const headline = assetsGroup.headlines?.[0]?.value;
  if (!headline || headline.trim() === "") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "headlines", 0, "value"],
      message: i18next.t(FIELD_REQUIRED),
    });
  } else if (
    headline.length > ADS_CAMPAIGN_PLATFORM_HEADLINE_MAX_LENGTH[LINKEDIN]
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "headlines", 0, "value"],
      message: i18next.t(FIELD_MAX_LENGTH, {
        value: ADS_CAMPAIGN_PLATFORM_HEADLINE_MAX_LENGTH[LINKEDIN],
      }),
    });
  }

  // Media (mediaUrls[0]) - required
  const mediaUrl = assetsGroup.mediaUrls?.[0];
  if (!mediaUrl || mediaUrl.trim() === "") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "mediaUrls", 0],
      message: i18next.t(FIELD_REQUIRED),
    });
  }
}

// Video format validation (SINGLE_VIDEO)
function validateVideoFormat({ assetsGroup, basicPaths, ctx }) {
  const destinationUrls = assetsGroup.destinationUrls || [];

  // callToAction - required for all formats
  if (!assetsGroup.callToAction) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "callToAction"],
      message: i18next.t(FIELD_REQUIRED),
    });
  }

  // destinationUrl - required
  const destinationUrl = destinationUrls[0]?.value;
  if (!destinationUrl || destinationUrl.trim() === "") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "destinationUrls", 0, "value"],
      message: i18next.t(FIELD_REQUIRED),
    });
  } else if (
    !REGEX_URL_WITH_PROTOCOL_REQUIRED.test(encodeURI(destinationUrl))
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "destinationUrls", 0, "value"],
      message: i18next.t(FIELD_URL_VALID_WITH_PROTOCOL),
    });
  }

  // Headline (headlines[0]) - required
  const headline = assetsGroup.headlines?.[0]?.value;
  if (!headline || headline.trim() === "") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "headlines", 0, "value"],
      message: i18next.t(FIELD_REQUIRED),
    });
  } else if (
    headline.length > ADS_CAMPAIGN_PLATFORM_HEADLINE_MAX_LENGTH[LINKEDIN]
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "headlines", 0, "value"],
      message: i18next.t(FIELD_MAX_LENGTH, {
        value: ADS_CAMPAIGN_PLATFORM_HEADLINE_MAX_LENGTH[LINKEDIN],
      }),
    });
  }

  // Media (mediaUrls[0]) - required
  const mediaUrl = assetsGroup.mediaUrls?.[0];
  if (!mediaUrl || mediaUrl.trim() === "") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "mediaUrls", 0],
      message: i18next.t(FIELD_REQUIRED),
    });
  }
}

// Carousel format validation (CAROUSEL)
function validateCarouselFormat({ assetsGroup, basicPaths, ctx, objective }) {
  const mediaUrls = assetsGroup.mediaUrls || [];
  const headlines = assetsGroup.headlines || [];
  const destinationUrls = assetsGroup.destinationUrls || [];

  const minCards = ADS_CAMPAIGN_LINKEDIN_CAROUSEL_MIN_CARDS;
  const maxCards = ADS_CAMPAIGN_LINKEDIN_CAROUSEL_MAX_CARDS;

  // Determine max headline length based on objective
  const maxHeadlineLength =
    objective === ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION
      ? ADS_CAMPAIGN_LINKEDIN_CAROUSEL_CARD_HEADLINE_LEADS_MAX_LENGTH
      : ADS_CAMPAIGN_LINKEDIN_CAROUSEL_CARD_HEADLINE_MAX_LENGTH;

  // Validate minimum cards
  if (mediaUrls.length < minCards) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "mediaUrls"],
      message: i18next.t(FIELD_MIN_CARDS_AD_CAROUSEL, {
        value: minCards,
      }),
    });
  }

  // Validate maximum cards
  if (mediaUrls.length > maxCards) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...basicPaths, "mediaUrls"],
      message: i18next.t(FIELD_MAX_IMAGES_VALID, {
        value: maxCards,
      }),
    });
  }

  // Validate each card
  mediaUrls.forEach((mediaUrl, index) => {
    // Media URL required for each card
    if (!mediaUrl || (typeof mediaUrl === "string" && mediaUrl.trim() === "")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "mediaUrls", index],
        message: i18next.t(FIELD_REQUIRED),
      });
    }

    // Destination URL required for each card
    const destUrl = destinationUrls[index]?.value;
    if (!destUrl || destUrl.trim() === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "destinationUrls", index, "value"],
        message: i18next.t(FIELD_REQUIRED),
      });
    } else if (!REGEX_URL_WITH_PROTOCOL_REQUIRED.test(encodeURI(destUrl))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "destinationUrls", index, "value"],
        message: i18next.t(FIELD_URL_VALID_WITH_PROTOCOL),
      });
    }

    // Headline required for each card
    const headline = headlines[index]?.value;
    if (!headline || headline.trim() === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "headlines", index, "value"],
        message: i18next.t(FIELD_REQUIRED),
      });
    } else if (headline.length > maxHeadlineLength) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...basicPaths, "headlines", index, "value"],
        message: i18next.t(FIELD_MAX_LENGTH, {
          value: maxHeadlineLength,
        }),
      });
    }
  });
}
